import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { PageHeader } from "@/components/ui/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import { CeldaPrincipal, DataTable, Icon, StatusBadge, Tip, type EstadoDS } from "@/components/ds";
import { AlertCircle, Loader2, Plus } from "@/components/ds/icons";
import { ProgramaFilaExpandida } from "@/components/capacitaciones-ia/ProgramaFilaExpandida";
import { NuevoProgramaDrawer } from "@/components/capacitaciones-ia/NuevoProgramaDrawer";
import { ProgramarEnviosDrawer } from "@/components/capacitaciones-ia/ProgramarEnviosDrawer";
import { EnviarAhoraDrawer } from "@/components/capacitaciones-ia/EnviarAhoraDrawer";
import { CapacitacionesKpiHeader } from "@/components/capacitaciones-ia/CapacitacionesKpiHeader";
import { NivelPrograma } from "@/components/capacitaciones-ia/NivelPrograma";
import {
  PLAN_DE_ENVIOS_HABILITADO,
  PlanDeEnvios,
} from "@/components/capacitaciones-ia/PlanDeEnvios";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { capacitacionesIaApi } from "@/lib/api/capacitaciones-ia";
import { ApiError } from "@/lib/api";
import { usePollSolicitud } from "@/hooks/usePollSolicitud";
import { BADGES_REFRESH_EVENT } from "@/hooks/useSidebarBadges";
import type {
  AsignacionesKpis,
  DestinatariosOpciones,
  Programa,
  ProgramaEstado,
  ProgramasResponse,
  ProgramaTematica,
  TematicaDestinatarioRef,
  TematicaDestinatarioTipo,
} from "@/types/capacitaciones-ia";
import { toast } from "sonner";

type VistaPrograma = "lista" | "plan";

/** Estados que congelan el contenido como evidencia (design #1399, decisión 1). */
const ESTADOS_CONGELADOS: ProgramaEstado[] = ["activo", "finalizado", "archivado"];

/**
 * Estado del Programa para la columna "Estado" (design D4, WU7 —
 * sdd/capacitaciones-pantalla-unica): separa `activo` en "Programado"
 * (hay temáticas con fecha reservada, ninguna salió todavía) y "En curso"
 * (al menos una ya salió a terreno). El split es SÓLO de lectura en el
 * cliente — `estado` sigue siendo el accessor derivado que manda el
 * backend, esto únicamente afina cómo se pinta cuando vale `activo`.
 */
function estadoProgramaUI(programa: Programa): EstadoDS {
  if (programa.estado !== "activo") return programa.estado;
  const hayEnviada = programa.temas.some((t) => t.estado_negocio === "enviada");
  return hayEnviada ? "en_curso" : "programado";
}

/** `generando` es el único estado que cambia SOLO — lo resuelve el webhook de
 * la IA en el backend, sin que el usuario toque nada acá. Por eso es el
 * disparador del refresco automático (ver `hayGenerando` más abajo). */
function estaGenerando(programas: Programa[]): boolean {
  return programas.some((p) => p.estado === "generando");
}

/** Estado de la TEMÁTICA para el filtro del listado fusionado — no del
 * Programa (ese vive en la columna "Estado" y las acciones de fila, ver
 * WU7/D4). "Completada" no es un valor de `estado_negocio`: se deriva EN
 * CLIENTE sobre una temática ya enviada donde todos respondieron. */
type FiltroTematica = "todas" | "pendiente" | "programada" | "enviada" | "completada";

/**
 * Denominador congelado al enviar (design D10): lee `destinatarios_total`/
 * `respondieron` tal como los entrega `/programas` (ya plegados en el
 * backend por `ResultadosTematicaService`, WU2) — nunca los recalcula
 * contra la dotación activa hoy. Un trabajador desactivado después del
 * envío no le quita a la temática su 100%.
 */
function tematicaEsCompletada(tema: ProgramaTematica): boolean {
  return (
    tema.estado_negocio === "enviada" &&
    tema.destinatarios_total > 0 &&
    tema.respondieron === tema.destinatarios_total
  );
}

/**
 * Sirve a las dos formas que manda el backend: `creado_at` como ISO8601
 * completo y `fecha_programada` como "YYYY-MM-DD".
 *
 * El `T00:00:00` es obligatorio para la segunda: sin él,
 * `new Date("2026-08-17")` es medianoche UTC, que en Chile (UTC-4) cae el 16.
 * Mismo criterio que `PlanDeEnvios.parseFechaLocal`.
 */
function formatFecha(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso.includes("T") ? iso : `${iso}T00:00:00`).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** "Reemplazado por vN" cuando existe la versión siguiente en la misma
 * familia; si no, "Creado el …". Las versiones nacen consecutivas
 * (`crearVersion()` siempre usa `max(version)+1`), así que el sucesor
 * directo de una versión archivada es, por construcción, `version + 1`. */
function ultimaActividad(programa: Programa, todos: Programa[]): string {
  if (programa.estado === "archivado") {
    const sucesor = todos.find(
      (p) =>
        p.programa_raiz_id === programa.programa_raiz_id && p.version === programa.version + 1
    );
    if (sucesor) return `Reemplazado por v${sucesor.version}`;
  }
  return `Creado el ${formatFecha(programa.creado_at)}`;
}

/**
 * Resumen de la columna "Programa" (celda colapsada) — la fuente primaria
 * (`fuentes[0]`, `orden = 0`) más un "+N" cuando hay secundarias
 * (sdd/nuevo-programa-multifuente). `documento` (deprecado) sólo cubre el
 * caso defensivo de `fuentes` vacío; un Programa ad-hoc-only ya no cae en
 * "—" como pasaba cuando esta celda sólo miraba `documento`.
 *
 * `?.[0]` en vez de `[0]` — probado con CDP real: un Programa cuya respuesta
 * todavía no trae `fuentes` (p. ej. mezcla de versiones API/cliente durante
 * un rollout) tira la tabla ENTERA con `Cannot read properties of undefined`
 * si se indexa sin optional chaining primero.
 */
function metaFuentePrincipal(programa: Programa): string {
  const primaria =
    programa.fuentes?.[0]?.etiqueta ?? programa.documento?.label ?? programa.documento?.filename_original ?? null;
  if (!primaria) return "—";
  const extra = (programa.fuentes?.length ?? 0) - 1;
  return extra > 0 ? `${primaria} +${extra} más` : primaria;
}

/**
 * Barra de "Enviadas" — a propósito NO es la `ProgressBar` del DS. Esa
 * componente colorea por umbral de cumplimiento (rojo bajo 50%), que tiene
 * sentido para "% de EPP entregado" pero no acá: un programa recién armado
 * con 0/10 enviadas está SANO, no en alerta (SPEC: el plan se arma para
 * ~6 meses y se monitorea de a poco). Mismo criterio de color que el
 * prototipo: teal mientras avanza, verde sólo al completarse, gris en 0.
 *
 * El `w-full` NO es decorativo. La celda del DataTable es `display:flex`, así
 * que un hijo sin `flex-grow` se encoge a su contenido — y el riel de la barra
 * es un div vacío cuyo `flex-1` aporta 0 al ancho intrínseco del padre. Sin
 * esto, la barra existe en el DOM pero mide 0px de ancho: se ve el "3/8" y
 * nada más. */
function BarraEnviadas({ valor, total }: { valor: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((valor / total) * 100)) : 0;
  const color = pct === 100 ? "var(--success-bar)" : pct > 0 ? "var(--primary)" : "var(--border-strong)";
  return (
    <div className="flex w-full min-w-0 items-center gap-2 pr-3">
      <div className="bg-border-subtle h-1.5 min-w-0 flex-1 overflow-hidden rounded-full">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="fc-num text-muted-foreground shrink-0 text-[11px]">
        {valor}/{total}
      </span>
    </div>
  );
}

export function ProgramaCapacitaciones() {
  const search = useSearch();
  const [, navigate] = useLocation();

  const [programas, setProgramas] = useState<Programa[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Cabecera de 3 KPIs (D9, WU5) — `null` mientras no llegó la primera
  // respuesta; el header no renderiza nada en ese caso (ver
  // `CapacitacionesKpiHeader`). Un error acá no bloquea el resto de la
  // pantalla: sólo el header queda vacío, la tabla sigue funcionando.
  const [kpis, setKpis] = useState<AsignacionesKpis | null>(null);

  // `?estado=` precarga el filtro — es el destino del repunte del
  // Dashboard: "Capacitaciones completadas", "Con respuestas pendientes" y
  // "Trabajadores sin responder" apuntan acá ahora que sus pantallas propias
  // se retiraron (WU4). Un valor desconocido o ausente cae en "todas".
  const [filtro, setFiltro] = useState<FiltroTematica>(() => {
    const v = new URLSearchParams(search).get("estado");
    return v === "pendiente" || v === "programada" || v === "enviada" || v === "completada"
      ? v
      : "todas";
  });

  // Quitar una temática que todavía no salió. Pasa por confirmación porque el
  // backend la borra de verdad: no hay papelera de la que rescatarla.
  const [aQuitar, setAQuitar] = useState<ProgramaTematica | null>(null);
  const [quitando, setQuitando] = useState(false);

  // "Enviar ahora" — adelanta una temática programada. Pasa por confirmación
  // porque el correo sale a terreno en el acto y no hay forma de recogerlo.
  const [aEnviarAhora, setAEnviarAhora] = useState<ProgramaTematica | null>(null);
  const [enviandoAhora, setEnviandoAhora] = useState(false);

  // "Eliminar programa" (D5/WU7) — único camino de recuperación de un
  // programa `fallido`. Confirmación porque es destructivo e irreversible.
  const [aEliminar, setAEliminar] = useState<Programa | null>(null);
  const [eliminandoPrograma, setEliminandoPrograma] = useState(false);

  // D3 — drawer "Nuevo programa". Guardamos el id de lo recién creado para
  // que el drawer pueda mostrar el estado REAL de ese programa (generando →
  // listo / fallido) con el mismo dato que la tabla, sin abrir un polling
  // propio en paralelo.
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [programaCreadoId, setProgramaCreadoId] = useState<number | null>(null);

  // E3 — drawer "Programar envíos" (spec §3.4).
  const [drawerEnvioAbierto, setDrawerEnvioAbierto] = useState(false);
  const [programaInicialEnvioId, setProgramaInicialEnvioId] = useState<number | null>(null);
  const [opciones, setOpciones] = useState<DestinatariosOpciones | null>(null);

  // E4 — toggle Lista / Plan de envíos (spec §3) y el mes visible del calendario.
  // `?vista=plan` en la URL abre directo en el calendario — es el destino de
  // "Ver el plan de lo que falta" desde Pantalla 3 (Ola 7, F2, spec §4).
  //
  // Con el calendario oculto (`PLAN_DE_ENVIOS_HABILITADO`) el parámetro se
  // ignora: un enlace viejo o un favorito no puede dejar la pantalla en una
  // vista que ya no se ofrece.
  const [vista, setVista] = useState<VistaPrograma>(() =>
    PLAN_DE_ENVIOS_HABILITADO && new URLSearchParams(search).get("vista") === "plan"
      ? "plan"
      : "lista"
  );
  const [mesCalendario, setMesCalendario] = useState(() => new Date());

  /**
   * `silencioso` refresca sin pasar por el spinner de pantalla completa. Se
   * usa al crear un programa: el drawer sigue abierto encima y hacer parpadear
   * la tabla de atrás no aporta nada.
   */
  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setIsLoading(true);
    setLoadError(null);
    try {
      const [resProgramas, resOpciones] = await Promise.all([
        capacitacionesIaApi.getProgramas(),
        // Padrón real para "Llega a N" (drawer) y el subtítulo de cada
        // bloque del calendario — nunca un número escrito a mano.
        capacitacionesIaApi.getDestinatariosOpciones(),
      ]);
      setProgramas(resProgramas.data);
      setOpciones(resOpciones);
      // La cabecera de KPIs (D9, WU5) se pide APARTE, sin `await`: un error
      // acá no debe tumbar la tabla completa, sólo dejar el header vacío
      // (`kpis` se queda en `null`, que `CapacitacionesKpiHeader` maneja).
      capacitacionesIaApi
        .getAsignacionesKpis()
        .then(setKpis)
        .catch(() => setKpis(null));
    } catch (err) {
      setLoadError(
        err instanceof ApiError && err.message
          ? err.message
          : "No se pudo cargar el programa de capacitaciones."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ---------------------------------------------------------------------------
  // Refresco automático mientras la IA genera temáticas.
  //
  // `POST /programas` devuelve el Programa en "Generando" y la generación
  // termina en el backend (webhook `planificador.completed`), no acá. Sin este
  // polling la fila se quedaba latiendo en "Generando" hasta que el usuario
  // recargaba la página a mano — la animación del StatusBadge prometía una
  // liveness que la pantalla no cumplía.
  //
  // Refresca en silencio: escribe `programas` sin tocar `isLoading`, así la
  // tabla nunca parpadea a spinner mientras el usuario la está mirando.
  // ---------------------------------------------------------------------------
  const hayGenerando = useMemo(() => estaGenerando(programas), [programas]);

  const traerProgramas = useCallback(() => capacitacionesIaApi.getProgramas(), []);
  const generacionTerminada = useCallback(
    (res: ProgramasResponse) => !estaGenerando(res.data),
    []
  );

  // 45 polls ≈ 11 min de cobertura (3s los primeros 30s, 10s hasta los 5 min,
  // 60s después). La generación real tarda menos de un minuto; el margen cubre
  // una cola cargada sin dejar la pestaña consultando para siempre — el
  // backend recién da por perdida la generación a los 45 min
  // (`fc:capacitaciones-programas-colgados`).
  const { data: programasPoll, capAlcanzado } = usePollSolicitud({
    fetcher: traerProgramas,
    isTerminal: generacionTerminada,
    enabled: hayGenerando,
    maxPolls: 45,
  });

  useEffect(() => {
    if (!programasPoll) return;
    setProgramas(programasPoll.data);
    // El badge "IA" del sidebar sale del mismo hecho de negocio: si acá ya no
    // queda nada generando, dejarlo latiendo sería contradecir a la tabla.
    if (!estaGenerando(programasPoll.data)) {
      window.dispatchEvent(new CustomEvent(BADGES_REFRESH_EVENT));
    }
  }, [programasPoll]);

  function abrirDrawerEnvio(programaId: number | null) {
    setProgramaInicialEnvioId(programaId);
    setDrawerEnvioAbierto(true);
  }

  /** Tras confirmar en el drawer: refresca, cambia a Plan de envíos y deja
   * el mes del envío visible (spec §3.4 — "dejar al usuario en el Plan de
   * envíos con ese programa visible", no en otra pantalla).
   *
   * Con el calendario oculto no hay adónde saltar: se queda en la lista, que
   * ya muestra la fecha programada de cada temática al expandir la fila. */
  function alProgramar(_programaId: number, fechaReferencia: Date) {
    cargar();
    if (PLAN_DE_ENVIOS_HABILITADO) {
      setVista("plan");
      setMesCalendario(fechaReferencia);
    }
  }

  // El archivado sigue sin chip propio (D3/WU1): sólo lo produce el
  // versionado, apagado — nunca hay uno que mostrar u ocultar.
  const programasVigentes = useMemo(
    () => programas.filter((p) => p.estado !== "archivado"),
    [programas]
  );

  const tematicasVigentes = useMemo(
    () => programasVigentes.flatMap((p) => p.temas),
    [programasVigentes]
  );

  const conteos = useMemo(
    () => ({
      todas: tematicasVigentes.length,
      pendiente: tematicasVigentes.filter((t) => t.estado_negocio === "pendiente").length,
      programada: tematicasVigentes.filter((t) => t.estado_negocio === "programada").length,
      enviada: tematicasVigentes.filter((t) => t.estado_negocio === "enviada").length,
      completada: tematicasVigentes.filter(tematicaEsCompletada).length,
    }),
    [tematicasVigentes]
  );

  // El filtro es por TEMÁTICA (spec: Por programar/Programadas/Enviadas +
  // Completadas derivada), aplicado sobre las filas de Programa: un
  // programa se muestra si tiene al menos una temática que coincide — la
  // fila expandida sigue mostrando TODAS sus temáticas como evidencia, sin
  // recortar por el filtro activo.
  const filas = useMemo(() => {
    if (filtro === "todas") return programasVigentes;
    if (filtro === "completada") {
      return programasVigentes.filter((p) => p.temas.some(tematicaEsCompletada));
    }
    return programasVigentes.filter((p) => p.temas.some((t) => t.estado_negocio === filtro));
  }, [programasVigentes, filtro]);

  /** El Programa dueño de la temática que se está por enviar — el drawer
   * necesita su nombre y el conteo de temáticas. Se busca sobre `programas`
   * (no sobre `programasVigentes`) para que un refresco que archive el
   * programa con el drawer abierto no lo deje sin encabezado. */
  const programaDeTematica = useMemo(
    () =>
      aEnviarAhora === null
        ? null
        : (programas.find((p) => p.temas.some((t) => t.id === aEnviarAhora.id)) ?? null),
    [programas, aEnviarAhora]
  );

  /** Adelanta la temática confirmada: sale ahora y deja libre el día que
   * tenía reservado. El backend es quien mueve la fecha — acá sólo se
   * recarga para que la fila y el calendario cuenten lo mismo.
   *
   * `alcance` llega con valor sólo desde el estado SIN programación del
   * drawer; una temática programada lo manda en `null` porque su audiencia
   * ya está congelada. */
  async function enviarAhora(
    alcance: { tipo: TematicaDestinatarioTipo; refs: TematicaDestinatarioRef[] } | null
  ) {
    if (!aEnviarAhora) return;
    const estabaProgramada = aEnviarAhora.estado_negocio === "programada";
    if (!estabaProgramada && alcance !== null && alcance.tipo !== "empresa" && alcance.refs.length === 0) {
      toast.info("Elige al menos un destinatario.");
      return;
    }
    setEnviandoAhora(true);
    try {
      // Dos endpoints porque son dos operaciones distintas, no dos nombres
      // de la misma. `enviar-ahora` ADELANTA una temática con día reservado:
      // reprograma primero y por eso rechaza (422) una sin
      // `fecha_programada`. `enviar` dispara una que nunca se agendó y no
      // toca el calendario, porque no hay nada que liberar.
      if (estabaProgramada) {
        await capacitacionesIaApi.enviarTematicaAhora(aEnviarAhora.id);
      } else {
        await capacitacionesIaApi.enviar(
          aEnviarAhora.id,
          alcance
            ? {
                destinatario_tipo: alcance.tipo,
                destinatario_refs: alcance.tipo === "empresa" ? [] : alcance.refs,
              }
            : undefined
        );
      }
      toast.success(
        estabaProgramada
          ? `«${aEnviarAhora.titulo}» se está generando y sale por correo en unos minutos. El día que tenía reservado quedó libre.`
          : `«${aEnviarAhora.titulo}» se está generando y sale por correo en unos minutos.`
      );
      setAEnviarAhora(null);
      await cargar();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.message
          ? err.message
          : estabaProgramada
            ? "No se pudo enviar la temática. La programación quedó como estaba."
            : "No se pudo enviar la temática. Quedó como estaba, sin programar."
      );
    } finally {
      setEnviandoAhora(false);
    }
  }

  /** Saca del programa una temática que todavía no salió. El backend vuelve a
   * chequear los dos guards (no congelada, no la única): si el estado cambió
   * con la fila abierta —por ejemplo, el cron la envió mientras el diálogo
   * estaba puesto— responde 422 y se recarga para mostrar la realidad. */
  async function quitarTematica() {
    if (!aQuitar) return;
    setQuitando(true);
    try {
      await capacitacionesIaApi.eliminarTematica(aQuitar.id);
      toast.success(`«${aQuitar.titulo}» salió del programa.`);
      setAQuitar(null);
      await cargar();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.message
          ? err.message
          : "No se pudo quitar la temática."
      );
      await cargar();
    } finally {
      setQuitando(false);
    }
  }

  /** Borra el programa `fallido` confirmado y libera el documento fuente
   * para volver a intentarlo desde "Nuevo programa" (D5). El backend
   * responde 422 si el estado cambió mientras el diálogo estaba abierto —
   * se recarga igual para que la fila muestre la realidad actual. */
  async function eliminarPrograma() {
    if (!aEliminar) return;
    setEliminandoPrograma(true);
    try {
      await capacitacionesIaApi.eliminarPrograma(aEliminar.id);
      toast.success("Programa eliminado. El documento quedó libre para un nuevo intento.");
      setAEliminar(null);
      await cargar();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.message
          ? err.message
          : "No se pudo eliminar el programa."
      );
      await cargar();
    } finally {
      setEliminandoPrograma(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Programas de Capacitación"
        subtitle="Temáticas que salen por correo a tus trabajadores y vuelven como evidencia."
        rightContent={
          <div className="flex shrink-0 items-center gap-2.5">
            {/* El grupo entero se oculta, no sólo la opción "Plan de envíos":
                un selector de una sola opción es ruido y delata que existe una
                vista escondida (ver `PLAN_DE_ENVIOS_HABILITADO`). */}
            {PLAN_DE_ENVIOS_HABILITADO && (
              <ToggleGroup
                type="single"
                variant="segmented"
                value={vista}
                onValueChange={(v) => v && setVista(v as VistaPrograma)}
              >
                <ToggleGroupItem value="lista">
                  <Icon name="format_list_bulleted" size={16} />
                  Lista
                </ToggleGroupItem>
                <ToggleGroupItem value="plan">
                  <Icon name="calendar_month" size={16} />
                  Plan de envíos
                </ToggleGroupItem>
              </ToggleGroup>
            )}
            <Button
              onClick={() => {
                // Sin esto, abrir el drawer una segunda vez arrastraría el
                // programa de la creación anterior.
                setProgramaCreadoId(null);
                setDrawerAbierto(true);
              }}
            >
              <Plus className="size-4" />
              Nuevo programa
            </Button>
          </div>
        }
      />

      <CapacitacionesKpiHeader kpis={kpis} formatFecha={formatFecha} />

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 pt-6 text-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p className="text-sm text-muted-foreground">{loadError}</p>
            <button
              type="button"
              onClick={() => cargar()}
              className="text-primary-hover text-sm underline"
            >
              Reintentar
            </button>
          </CardContent>
        </Card>
      ) : vista === "plan" ? (
        <PlanDeEnvios
          programas={programas}
          opciones={opciones}
          mes={mesCalendario}
          onCambiarMes={setMesCalendario}
        />
      ) : (
        <Card>
          <CardContent className="pt-6">
            <DataTable
              rows={filas}
              getRowKey={(p) => p.id}
              // 260 (programa, mínimo) + 128 + 94 + 148 + 138 + 276 (Acción,
              // hasta 2 botones a la vez). "Nivel" se retiró como columna —
              // vive junto al nombre (`NivelPrograma`) — y el mínimo bajó con
              // ella: dejarlo en 1086 mantendría el scroll horizontal de una
              // columna que ya no está.
              //
              // Los 276px de "Acción" son medidos, no estimados: con la
              // tipografía del DS (Geist, `xs` = 12.5px/500, padding 11px),
              // "Ver programación" con su ícono mide 149.8px y "Programar
              // serie" 117.3px — 273.1px con el `gap-1.5` del medio. Los 210
              // anteriores ya se desbordaban ~10px cuando aparecían los dos
              // botones juntos, que es el estado normal de un programa a
              // medio ejecutar.
              minWidth={1044}
              pageSize={20}
              emptyMessage={
                programas.length === 0
                  ? "Todavía no hay programas de capacitación."
                  : "Ningún programa coincide con el filtro."
              }
              emptyIcon="school"
              toolbar={
                <ToggleGroup
                  type="single"
                  variant="segmented"
                  value={filtro}
                  onValueChange={(v) => v && setFiltro(v as FiltroTematica)}
                >
                  <ToggleGroupItem value="todas">
                    Todas
                    <ToggleGroupCount>{conteos.todas}</ToggleGroupCount>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="pendiente">
                    Por programar
                    <ToggleGroupCount>{conteos.pendiente}</ToggleGroupCount>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="programada">
                    Programadas
                    <ToggleGroupCount>{conteos.programada}</ToggleGroupCount>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="enviada">
                    Enviadas
                    <ToggleGroupCount>{conteos.enviada}</ToggleGroupCount>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="completada">
                    Completadas
                    <ToggleGroupCount>{conteos.completada}</ToggleGroupCount>
                  </ToggleGroupItem>
                </ToggleGroup>
              }
              renderExpanded={(programa) => (
                <ProgramaFilaExpandida
                  programa={programa}
                  congelado={ESTADOS_CONGELADOS.includes(programa.estado)}
                  esArchivado={programa.estado === "archivado"}
                  onCrearVersion={() => {}}
                  creandoVersion={false}
                  onAbrirFicha={(tema) =>
                    navigate(`/capacitaciones/programa/tematica/${tema.id}`)
                  }
                  // Cualquier temática que todavía no salió puede enviarse
                  // hoy, tenga día reservado o no — mismo criterio que el
                  // prototipo (`puedeEnviar: !enviada`). Las ya enviadas no
                  // llegan hasta acá: `ProgramaFilaExpandida` no le pasa
                  // acciones a una temática congelada.
                  renderAccionesTematica={(tema) =>
                    tema.estado_negocio !== "enviada" ? (
                      <>
                        <Tip
                          texto={
                            tema.estado_negocio === "programada"
                              ? "Enviar esta temática hoy, fuera del calendario de la serie"
                              : "Enviar esta temática hoy, sin esperar a programarla"
                          }
                        >
                          {/* Teal suave, como en el prototipo: es la acción
                              que EMPUJA la temática a terreno, y se lee
                              distinta de "Ver capacitación", que sólo abre
                              evidencia ya emitida. No rompe la regla de "un
                              solo botón teal por pantalla": esa habla del
                              primario sólido (`variant="default"`), y el
                              primario de esta pantalla sigue siendo "Nuevo
                              programa". */}
                          <Button
                            variant="outline"
                            size="xs"
                            className="border-primary-200 bg-primary-50 text-primary-hover hover:bg-primary-100 hover:text-primary-hover"
                            onClick={() => setAEnviarAhora(tema)}
                          >
                            <Icon name="send" size={15} />
                            Enviar ahora
                          </Button>
                        </Tip>
                        {/* La última no se puede quitar: un programa sin
                            temáticas no tiene nada que enviar y el único
                            camino de vuelta es regenerar el temario. El
                            backend lo rechaza igual (422) — acá se oculta el
                            botón para no ofrecer algo que va a fallar. */}
                        {programa.temas.length > 1 && (
                          <Tip texto="Quitar esta temática del programa">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Quitar «${tema.titulo}» del programa`}
                              className="text-soft hover:bg-destructive-bg hover:text-destructive"
                              onClick={() => setAQuitar(tema)}
                            >
                              <Icon name="close" size={16} />
                            </Button>
                          </Tip>
                        )}
                      </>
                    ) : null
                  }
                />
              )}
              columns={[
                {
                  key: "programa",
                  header: "Programa",
                  width: "minmax(260px,1fr)",
                  cell: (p) => (
                    <CeldaPrincipal
                      titulo={p.nombre ?? "Programa sin nombre"}
                      // El nivel dejó de ser columna propia: era un dato
                      // constante por programa que gastaba 108px de ancho en
                      // una tabla que ya se desborda. Como indicador junto al
                      // nombre se lee en el mismo golpe de vista y libera esa
                      // columna para lo que sí cambia.
                      sufijoTitulo={<NivelPrograma nivel={p.nivel} />}
                      // Resumen de UNA línea, no la lista completa — esa vive
                      // en la fila expandida (`ProgramaFilaExpandida`, con su
                      // chip por fuente y la descarga de las ad-hoc). Acá sólo
                      // se ancla en la fuente primaria (`fuentes[0]`, orden 0)
                      // y, si hay más, se lo dice con un "+N" en vez de dejar
                      // que el resto se pierda truncado.
                      meta={metaFuentePrincipal(p)}
                      icono="description"
                    />
                  ),
                },
                {
                  key: "estado",
                  header: "Estado",
                  width: "128px",
                  cell: (p) => <StatusBadge estado={estadoProgramaUI(p)} />,
                },
                {
                  key: "tematicas",
                  header: "Temáticas",
                  width: "94px",
                  cell: (p) => (
                    <span className="fc-num text-[12.5px]">
                      {p.temas.length > 0 ? p.temas.length : p.cantidad_tematicas}
                    </span>
                  ),
                },
                {
                  key: "enviadas",
                  header: "Enviadas",
                  width: "148px",
                  cell: (p) => {
                    const total = p.temas.length > 0 ? p.temas.length : p.cantidad_tematicas;
                    const enviadas = p.temas.filter((t) => t.estado_negocio === "enviada").length;
                    return <BarraEnviadas valor={enviadas} total={total} />;
                  },
                },
                {
                  key: "actividad",
                  header: "Última actividad",
                  width: "138px",
                  cell: (p) => (
                    <span className="fc-truncate text-soft text-[11.5px]">
                      {ultimaActividad(p, programas)}
                    </span>
                  ),
                },
                {
                  key: "accion",
                  header: "Acción",
                  width: "276px",
                  align: "right",
                  cell: (p) => {
                    // Fallido: eliminar es la ÚNICA acción — no hay retry,
                    // el documento fuente se libera para "Nuevo programa" (D5).
                    if (p.estado === "fallido") {
                      return (
                        <Button
                          variant="destructive"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAEliminar(p);
                          }}
                        >
                          Eliminar programa
                        </Button>
                      );
                    }

                    if (p.estado !== "listo" && p.estado !== "activo") return null;
                    // Un programa sin temáticas no tiene ni qué programar ni
                    // qué mostrar: la celda queda vacía en vez de ofrecer una
                    // acción que abre un drawer sin contenido.
                    if (p.temas.length === 0) return null;

                    const quedaPorProgramar = p.temas.some(
                      (t) => t.estado_negocio === "pendiente"
                    );
                    // "Ver programación" ya no es el reverso de "Programar":
                    // ambas aparecen a la vez si corresponde (D4) — antes un
                    // solo botón ocultaba "Ver programación" mientras quedara
                    // algo pendiente, aunque OTRAS temáticas ya estuvieran
                    // programadas o enviadas.
                    const hayProgramacionQueVer = p.temas.some(
                      (t) => t.estado_negocio !== "pendiente"
                    );

                    return (
                      <div className="flex justify-end gap-1.5">
                        {hayProgramacionQueVer && (
                          <Tip texto="Ver cómo quedó programada la serie">
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                abrirDrawerEnvio(p.id);
                              }}
                            >
                              <Icon name="event_available" size={15} />
                              Ver programación
                            </Button>
                          </Tip>
                        )}
                        {quedaPorProgramar && (
                          <Tip texto="Programar la serie completa: todas las temáticas sin fecha, una por período">
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                abrirDrawerEnvio(p.id);
                              }}
                            >
                              Programar serie
                            </Button>
                          </Tip>
                        )}
                      </div>
                    );
                  },
                },
              ]}
            />
          </CardContent>
        </Card>
      )}

      <NuevoProgramaDrawer
        abierto={drawerAbierto}
        onCerrar={() => setDrawerAbierto(false)}
        onCreado={(programaId) => {
          setProgramaCreadoId(programaId);
          cargar(true);
        }}
        programaCreado={programas.find((p) => p.id === programaCreadoId) ?? null}
        autoRefresco={!capAlcanzado}
      />

      <ProgramarEnviosDrawer
        abierto={drawerEnvioAbierto}
        onCerrar={() => setDrawerEnvioAbierto(false)}
        programas={programas}
        programaInicialId={programaInicialEnvioId}
        opciones={opciones}
        onProgramado={alProgramar}
      />

      {/* Confirmar: quitar una temática que todavía no salió. Es hard delete
          en el backend, no papelera: el aviso tiene que decirlo, porque en
          otras pantallas de la app "eliminar" sí es recuperable. */}
      <AlertDialog
        open={aQuitar !== null}
        onOpenChange={(open) => !open && !quitando && setAQuitar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quitar temática</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  «<strong>{aQuitar?.titulo}</strong>» sale del programa
                  {aQuitar?.estado_negocio === "programada" && (
                    <>
                      {" "}
                      y libera el{" "}
                      <strong>{formatFecha(aQuitar?.fecha_programada ?? null)}</strong>, que tenía
                      reservado
                    </>
                  )}
                  .
                </p>
                <p>
                  No se puede deshacer: no queda en una papelera. El resto del programa no se
                  toca, y el temario completo se puede volver a generar desde cero.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={quitando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                quitarTematica();
              }}
              disabled={quitando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {quitando ? "Quitando…" : "Quitar del programa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Enviar una temática hoy. Son dos consecuencias distintas y el drawer
          no puede ser el mismo: adelantar una PROGRAMADA libera el día que
          tenía reservado y respeta la audiencia que ya se congeló; disparar
          una SIN PROGRAMAR no toca ningún calendario, pero tampoco pasó nunca
          por la pantalla donde se eligen los destinatarios — así que hay que
          dejar elegirlos acá. */}
      <EnviarAhoraDrawer
        tema={aEnviarAhora}
        programa={programaDeTematica}
        opciones={opciones}
        enviando={enviandoAhora}
        onCerrar={() => !enviandoAhora && setAEnviarAhora(null)}
        onConfirmar={enviarAhora}
      />

      {/* Confirmar: eliminar un programa fallido (D5) — sin retry, se
          empieza de nuevo desde "Nuevo programa". */}
      <AlertDialog
        open={aEliminar !== null}
        onOpenChange={(open) => !open && !eliminandoPrograma && setAEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar programa</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  «<strong>{aEliminar?.nombre ?? "Programa sin nombre"}</strong>» no pudo
                  generarse:{" "}
                  {aEliminar?.falla_motivo ?? "la generación no llegó a completarse."}
                </p>
                <p>
                  Esta acción es irreversible. El documento fuente queda libre para intentarlo
                  de nuevo desde «Nuevo programa».
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminandoPrograma}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                eliminarPrograma();
              }}
              disabled={eliminandoPrograma}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminandoPrograma ? "Eliminando…" : "Eliminar programa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
